import React, { useState, useEffect } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { Link } from 'gatsby';

import CallToAction from '../../CallToAction';
import FormControl from './components/FormControl';
import FormSelect from './components/FormSelect';
import { getUrlParam } from '../Utilities/getUrlParam';

import { handleSubmit, validateOnChange } from './utils/helpers';
import truncateUrl from '../Utilities/truncateUrl';
import formatUrlValue from '../Utilities/formatUrlValue';

import usePreventNegativeInput from '../../../hooks/usePreventNegativeInput';

import {
  Container,
  Logo,
  Wrapper,
  Header,
  Form,
  Button,
  ErrorMessage,
  Footer,
  FooterLink,
  TwoColumn,
  SignUp,
} from './styles/index.styled';
import Loading from './components/Loading';
import UpKeepIcon from './components/UpKeepIcon';

const FreeTrialForm = ({ location, component }) => {
  const { t } = useTranslation();
  usePreventNegativeInput('Number_of_Technicians_Range__c');

  const [data, setData] = useState({
    firstname: '',
    lastname: '',
    phone: '',
    companyname: '',
    Number_of_Technicians_Range__c: 1,
    email: '',
    password: '',
    url: truncateUrl(location.href),
    jobtitle: '',
    industry: '',
    industrydetail: '',
    country: '',
    countrycode: '',
    state: '',
    gclid: '',
    language: component?.language || '',
    offer: component?.offer || '',
  });
  const [validation, setValidation] = useState({
    firstname: '',
    lastname: '',
    companyname: '',
    phone: '',
    Number_of_Technicians_Range__c: '',
    email: '',
    password: '',
  });
  const [networkError, setNetworkError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const url = formatUrlValue(location);

    setData(prevData => ({
      ...prevData,
      url: truncateUrl(url),
      gclid: getUrlParam('gclid'),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = e => {
    setData({ ...data, [e.target.name]: e.target.value });
    validateOnChange(e, e.target.name, validation, setValidation);
  };

  if (loading) return <Loading />;

  return (
    <Container>
      <Logo>
        <UpKeepIcon />
      </Logo>
      <Wrapper>
        <Header>
          <p className="heading">
            {component?.heading || t('Start your 7-day free trial today!')}
          </p>
          {component?.subhead && (
            <p className="subhead">{component?.subhead}</p>
          )}
        </Header>
        <Form
          id="trial-form"
          data-zi-mapped-form
          onSubmit={e =>
            handleSubmit(
              e,
              data,
              validation,
              setValidation,
              location,
              setLoading,
              setData,
              setNetworkError,
              component?.hubspotFormId
            )
          }
        >
          {/* Personal Information */}
          <TwoColumn>
            <FormControl
              id="firstname"
              type="text"
              name="firstname"
              placeholder={t('Enter your first name')}
              label={t('First Name')}
              required
              autoComplete="given-name"
              errorMessage={validation.firstname}
              value={data.firstname}
              handleChange={handleChange}
            />
            <FormControl
              id="lastname"
              type="text"
              name="lastname"
              label={t('Last Name')}
              placeholder={t('Enter your last name')}
              required
              autoComplete="family-name"
              errorMessage={validation.lastname}
              value={data.lastname}
              handleChange={handleChange}
            />
          </TwoColumn>
          <FormControl
            id="phone"
            type="tel"
            name="phone"
            label={t('Mobile Number')}
            placeholder={t('Enter your mobile number')}
            required
            autoComplete="off"
            errorMessage={validation.phone}
            value={data.phone}
            handleChange={handleChange}
          />
          <FormControl
            id="companyname"
            type="text"
            name="companyname"
            label={t('Company Name')}
            placeholder={t('Enter your company name')}
            required
            autoComplete="organization"
            errorMessage={validation.companyname}
            value={data.companyname}
            handleChange={handleChange}
          />
          <FormSelect
            id="Number_of_Technicians_Range__c"
            name="Number_of_Technicians_Range__c"
            label={t('Number of Technicians')}
            placeholder={t('Select the number of technicians')}
            value={data.Number_of_Technicians_Range__c}
            handleChange={handleChange}
            errorMessage={validation.Number_of_Technicians_Range__c}
            options={[
              { value: '', label: t('Select the number of technicians') },
              { value: '1-19', label: '1-19' },
              { value: '20-39', label: '20-39' },
              { value: '40+', label: '40+' },
            ]}
            helperText="A technician is a member of maintenance staff that manage tasks like work orders, asset info, inventory, etc."
            required
          />
          {/* Sign Up Section */}
          <SignUp>
            <p className="heading">Sign Up</p>
            <FormControl
              id="email"
              type="email"
              name="email"
              label={t('Email')}
              required
              placeholder={t('Enter your business email address')}
              autoComplete="email"
              errorMessage={validation.email}
              value={data.email}
              handleChange={handleChange}
            />
            <FormControl
              id="password"
              type="password"
              name="password"
              label={t('Password')}
              placeholder={t('Create Password')}
              required
              autoComplete="new-password"
              errorMessage={validation.password}
              value={data.password}
              handleChange={handleChange}
            />
          </SignUp>
          {/* Hidden Fields */}
          <FormControl
            id="jobtitle"
            type="text"
            name="jobtitle"
            label="Job Title"
            placeholder="Job Title"
            autoComplete="on"
            value={data.jobtitle}
            handleChange={handleChange}
            hidden
          />
          <FormControl
            id="industry"
            type="text"
            name="industry"
            label="Industry"
            placeholder="Industry"
            autoComplete="on"
            value={data.industry}
            handleChange={handleChange}
            hidden
          />
          <FormControl
            id="industrydetail"
            type="text"
            name="industrydetail"
            label="Industry Details"
            placeholder="Industry Details"
            autoComplete="on"
            value={data.industrydetail}
            handleChange={handleChange}
            hidden
          />
          <FormControl
            id="country"
            type="text"
            name="country"
            label="Country"
            placeholder="Country"
            autoComplete="country"
            value={data.country}
            handleChange={handleChange}
            hidden
          />
          <FormControl
            id="state"
            type="text"
            name="state"
            label="State"
            placeholder="State"
            autoComplete="state"
            value={data.state}
            handleChange={handleChange}
            hidden
          />
          {networkError && <ErrorMessage>{networkError}</ErrorMessage>}
          <Button type="submit">{t('Start a Free Trial')}</Button>
        </Form>
        <FooterLink>
          <p>{t('Not Ready Yet?')}</p>
          <CallToAction
            variant="Link - Blue"
            size="sm"
            url="https://www.upkeep.com/demo?referring_element=trial-form"
            label={t('Schedule a tour')}
          />
        </FooterLink>
        <Footer>
          <p>{t('No Credit Card Required.')}</p>
          <p>
            {t('By clicking above, you agree to the')}{' '}
            <Link to="/terms">{t('UpKeep Terms of Use')}.</Link>
          </p>
        </Footer>
      </Wrapper>
    </Container>
  );
};

export default FreeTrialForm;
